/**
 * L'accord nommé par ce que la main en prend, aux niveaux qui le fabriquent.
 *
 * Pur : ni React, ni DOM. C'est une lecture de l'harmonie du morceau, pas une
 * seconde analyse — la fondamentale, le degré et la place de l'accord dans la
 * grille ne bougent pas d'un pouce. Seule la **hauteur de la pile** change.
 *
 * ## Le nom promettait une note que le niveau ne pose pas
 *
 * Les trois premiers barreaux fabriquent leurs notes, et ils n'en fabriquent
 * que trois : `triadIntervals`, du côté de l'analyseur, garde la fondamentale,
 * la tierce (ou la suspension) et la quinte, et laisse tomber tout le reste.
 * Un `dominant7` arrive donc sous les doigts en triade majeure.
 *
 * Le nom, lui, venait de l'analyse sans savoir sur quel barreau il s'affichait.
 * L'écran écrivait « Ré 7 » à dix pixels de `Ré · Fa♯ · La`, une liste où la
 * septième est absente. C'est exactement ce que `chord-notes.tsx` s'interdit :
 * voir un nom et ses notes se contredire fait douter de la seule chose que cet
 * écran affirme.
 *
 * Deux façons de lever la contradiction, et une seule est vraie du niveau : ou
 * la main prend la septième, ce qui détruit le sujet du barreau — une seule
 * forme de main, la même d'un morceau à l'autre — ou le nom dit la triade. Le
 * nom dit la triade.
 *
 * ## Tout le nom, pas seulement le grand
 *
 * Le chiffrage du rail, le degré de la pastille, l'étiquette du rail et
 * l'inventaire du sas nomment tous le même accord, sur le même écran. En
 * corriger un seul déplacerait la contradiction au lieu de la lever. La
 * réduction se fait donc **une fois**, sur les accords que le studio descend au
 * niveau, et chaque surface en hérite sans rien connaître de ce fichier.
 *
 * C'est aussi ce qui la rend vérifiable : le morceau garde son harmonie, et
 * `withoutUnplayedExtension` est le seul endroit qui en dérive celle du niveau.
 *
 * ## Le degré perd son chiffre, pas son identité
 *
 * `II7` devient `II`, `viiø7` devient `vii°`. Le chiffre arabe d'un degré dit
 * ce qu'on empile ; le chiffre romain dit d'où l'accord part et ce qu'il fait
 * dans la tonalité, et ça, le barreau ne le change pas. La coupe se fait sur la
 * table de suffixes de l'analyseur, pas sur les caractères de la fin : `vii°7`
 * rogné d'un caractère donnerait `vii°`, mais `Imaj7` donnerait `Imaj`.
 *
 * ## Rebâtir le chiffrage, ici, et le rogner ailleurs
 *
 * `withoutWrittenBass` retire une basse d'un chiffrage par découpage, et se
 * refuse à le reconstruire : elle n'a rien à reconstruire, elle enlève. Ici la
 * qualité change, donc le suffixe change, et il n'y a pas d'autre moyen que de
 * réécrire `fondamentale + suffixe`. C'est ce que fait l'analyseur lui-même
 * quand il rétrograde un accord dont l'extension n'est pas jouée — voir
 * `demoteUnstatedExtension` — et la barre oblique y suit la même règle : une
 * basse qui n'est plus une note de l'accord ne peut plus être annoncée.
 *
 * ## Ce qu'on ne comprend pas, on n'y touche pas
 *
 * Une qualité absente de la table est rendue telle quelle. Elle vient d'une
 * analyse plus récente que ce fichier, et lui inventer une triade dirait au
 * joueur une chose qu'on ne sait pas.
 */

import type { AnalysisChord } from "@/lib/music/analyzer-types";
import { QUALITY_INTERVALS, ROOT_PITCH_CLASS } from "@/lib/music/theory-helpers";

/**
 * Les niveaux qui fabriquent leurs notes, par indice canonique dans
 * `LADDER_SHAPE`.
 *
 * Les trois premiers — accords, renversements, pulsation — et eux seuls : ce
 * sont ceux dont `model.go` pose la fondamentale à gauche et la triade à
 * droite. À partir de la basse publiée, les notes sont celles de la partition,
 * qui joue ses septièmes, et le nom du morceau redevient le nom du niveau.
 *
 * L'indice canonique et non le rang affiché, pour la même raison que
 * `isSasRung` et `isSeamRung` : un morceau à sept niveaux renumérote sans
 * trou et réduirait les mauvais.
 */
export function isModelRung(index: number): boolean {
  return index === 0 || index === 1 || index === 2;
}

/**
 * Les triades, par qualité, telles que l'analyseur les chiffre.
 *
 * Sept et pas quatre : une suspension remplace la tierce au lieu d'en manquer,
 * et un accord de quinte n'en a jamais eu. Toutes sont des cibles de réduction
 * possibles, et toutes sont déjà leur propre triade — un `sus4` traverse ce
 * fichier sans rien y perdre.
 *
 * Les symboles sont ceux de `ChordTemplates`, côté analyseur, parce que c'est
 * la convention qu'il applique partout ailleurs et qu'un chiffrage réécrit ici
 * doit être indiscernable d'un chiffrage qu'il aurait écrit lui-même.
 */
const TRIAD_SYMBOL: Readonly<Record<string, string>> = {
  major: "",
  minor: "m",
  diminished: "°",
  augmented: "+",
  sus4: "sus4",
  sus2: "sus2",
  power: "5",
};

/**
 * Le suffixe que chaque qualité pose derrière son chiffre romain.
 *
 * Copie de `RomanSuffixMap`, côté analyseur : c'est lui qui a écrit le degré
 * qu'on rogne, et deux tables qui divergeraient laisseraient un `Imaj7` intact
 * faute de reconnaître son suffixe. Une qualité absente n'en pose aucun.
 */
const ROMAN_SUFFIX: Readonly<Record<string, string>> = {
  dominant7: "7",
  "7": "7",
  maj7: "maj7",
  min7: "7",
  m7: "7",
  "half-dim7": "ø7",
  m7b5: "ø7",
  dim7: "°7",
  diminished: "°",
  augmented: "+",
  sus2: "sus2",
  sus4: "sus4",
  power: "5",
};

/**
 * La triade sous une qualité : fondamentale, tierce ou suspension, quinte.
 *
 * La règle de `triadIntervals`, côté analyseur, et pas une table écrite à la
 * main : c'est la même sélection, dans le même ordre, sur les mêmes intervalles.
 * Une tierce se cherche avant une suspension parce qu'un `add9` empile 0-2-4-7
 * et que prendre ses trois premiers intervalles rendrait un accord sans quinte.
 */
function triadIntervals(quality: string): number[] {
  const template = QUALITY_INTERVALS[quality];

  if (template === undefined) {
    return [];
  }

  const out = [0];

  for (const wanted of [
    [3, 4],
    [2, 5],
  ]) {
    const found = template.find((interval) => wanted.includes(interval));

    if (found !== undefined) {
      out.push(found);
      break;
    }
  }

  const fifth = template.find((interval) => interval === 6 || interval === 7 || interval === 8);

  if (fifth !== undefined) {
    out.push(fifth);
  }

  return out;
}

/**
 * La qualité de la triade que le niveau pose, ou la qualité reçue quand il n'y
 * a rien à retirer.
 *
 * Rendre la qualité d'origine est la réponse à trois questions différentes —
 * l'accord *est* une triade, sa forme est inconnue de la table, ou sa pile n'a
 * pas de triade à nommer — et l'appelant les traite pareil : il n'y touche pas.
 */
export function triadQuality(quality: string): string {
  const intervals = triadIntervals(quality);

  for (const name of Object.keys(TRIAD_SYMBOL)) {
    const triad = QUALITY_INTERVALS[name];

    if (
      triad !== undefined &&
      triad.length === intervals.length &&
      triad.every((interval, at) => interval === intervals[at])
    ) {
      return name;
    }
  }

  return quality;
}

/**
 * Le chiffre romain, son chiffre arabe échangé contre celui de la triade.
 *
 * Vide reste vide : l'analyseur ne répond pas des degrés de ce morceau, et un
 * degré fabriqué ici serait la seule chose fausse de l'écran.
 */
export function triadNumeral(numeral: string, quality: string, triad: string): string {
  if (numeral === "") {
    return "";
  }

  const worn = ROMAN_SUFFIX[quality] ?? "";
  const base = worn !== "" && numeral.endsWith(worn) ? numeral.slice(0, -worn.length) : numeral;

  return base + (ROMAN_SUFFIX[triad] ?? "");
}

/** L'accord réduit à sa triade : qualité, chiffrage, degré et basse annoncée. */
export function chordAsTriad(chord: AnalysisChord): AnalysisChord {
  const triad = triadQuality(chord.quality);

  if (triad === chord.quality) {
    return chord;
  }

  // La basse écrite peut être la note qu'on vient de retirer. Une barre oblique
  // qui nomme une note absente de l'accord est une affirmation pire que pas de
  // barre du tout, et c'est la règle que l'analyseur suit quand il rétrograde.
  const bassPitchClass = chord.bassNote === null ? undefined : ROOT_PITCH_CLASS[chord.bassNote];
  const keepsBass =
    bassPitchClass !== undefined &&
    triadIntervals(triad).includes((((bassPitchClass - chord.rootPitchClass) % 12) + 12) % 12);

  return {
    ...chord,
    quality: triad,
    symbol: chord.root + TRIAD_SYMBOL[triad] + (keepsBass ? `/${chord.bassNote}` : ""),
    romanNumeral: triadNumeral(chord.romanNumeral, chord.quality, triad),
    bassNote: keepsBass ? chord.bassNote : null,
  };
}

/**
 * Les accords du morceau, nommés comme ce niveau-ci les fait prendre.
 *
 * Hors des niveaux fabriqués, c'est la liste reçue, à l'identité près : la
 * partition joue ce que l'analyse a nommé, et il n'y a rien à réduire.
 */
export function withoutUnplayedExtension(
  chords: readonly AnalysisChord[],
  rungIndex: number,
): readonly AnalysisChord[] {
  return isModelRung(rungIndex) ? chords.map(chordAsTriad) : chords;
}
